// CTASection.jsx
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
const ShieldIcon = () => (
  <svg
    className="w-8 h-8 text-white"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);
const ArrowIcon = () => (
  <svg
    className="w-4 h-4"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const MailIcon = () => (
  <svg
    className="w-[17px] h-[17px] text-white/80"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const MotionLink = motion(Link);

const badges = [
  "SOC 2 In Progress",
  "GDPR Ready",
  "India Data Centers",
  "24/7 Monitoring",
];

function GridCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const W = canvas.parentElement.offsetWidth;
      const H = canvas.parentElement.offsetHeight;
      canvas.width = W * 2;
      canvas.height = H * 2;
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      const ctx = canvas.getContext("2d");
      ctx.scale(2, 2);
      const sz = 52;
      ctx.strokeStyle = "rgba(255,255,255,0.07)";
      ctx.lineWidth = 0.8;
      for (let x = 0; x <= W; x += sz) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, H);
        ctx.stroke();
      }
      for (let y = 0; y <= H; y += sz) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.stroke();
      }
    };
    setTimeout(draw, 80);
    window.addEventListener("resize", draw);
    return () => window.removeEventListener("resize", draw);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}

export default function CTASection() {
  return (
    <section
      className="relative flex items-center justify-center overflow-hidden px-6 py-24"
      style={{ background: "#1A4060" }}
    >
      <GridCanvas />

      <div className="relative z-10 flex flex-col items-center text-center max-w-[760px] w-full">
        {/* Shield Icon Box */}
        <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-white/10 backdrop-blur-sm mb-8 shadow-2xl">
          <ShieldIcon />
        </div>

        {/* Heading */}
        <h2 className="text-[clamp(32px,5.5vw,58px)] font-extrabold text-white leading-[1.1] tracking-[-0.03em] mb-5">
          Built for teams who value trust
        </h2>

        {/* Subtitle */}
        <p className="text-[clamp(14px,1.6vw,16px)] text-white/60 leading-[1.7] max-w-[540px] mb-9">
          From smart access controls to protected storage and privacy-first
          design, Simplifyte helps modern teams work with confidence.
        </p>

        {/* Buttons */}
        <div className="flex flex-wrap gap-3.5 justify-center mb-9 w-full">
          <MotionLink
            whileHover={{
              scale: 1.05,
              boxShadow: "0 20px 40px rgba(255,255,255,0.8)",
            }}
            to="/contact"
            className="inline-flex items-center justify-center gap-2.5 bg-white text-[#0D1B2A] text-[15px] font-bold rounded-[12px] px-7 py-[17px] flex-1 min-w-[220px] max-w-[300px] hover:opacity-90 transition-opacity no-underline"
          >
            Talk to Security Team
            <motion.div
              animate={{ x: [0, 10, 0] }}
              transition={{
                duration: 1.2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="hidden md:flex w-12 shrink-0 items-center justify-center relative z-20 mx-[-10px]"
            >
              <ArrowIcon />
            </motion.div>
          </MotionLink>
          <motion.a
            href="mailto:Support@simplifyte.com"
            className="inline-flex items-center justify-center gap-2.5 bg-white/10 text-white text-[15px] font-semibold rounded-[12px] px-7 py-[17px] border border-white/20 flex-1 min-w-[220px] max-w-[300px] hover:bg-white/15 transition-colors no-underline"
            whileHover={{
              scale: 1.05,
              boxShadow: "0 20px 40px rgba(255,255,255,0.15)",
            }}
          >
            <MailIcon />
            Support@simplifyte.com
          </motion.a>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap gap-2.5 justify-center">
          {badges.map((b) => (
            <div
              key={b}
              className="inline-flex items-center bg-white/8 border border-white/18 rounded-full px-[18px] py-2 text-[13px] font-medium text-white/80"
            >
              {b}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
